import React, {
  FC,
  MutableRefObject,
  useContext,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';

import { AriaAlertContext } from '../../AriaAlertContext';
import { colors } from '../constants';
import { cloneField, getFieldClone } from '../utilities/syntheticEventTracking';

import {
  ErrorMessageBubble,
  ErrorMessageBubbleIcon,
  ErrorMessageText,
  MessageBubble,
} from './MessageBubble';
import SubHeading from './SubHeading';

// TextInput
// Provides standard functionalities for formik-managed text inputs

// Interfaces -------------------------------------------------------------------------------------
export interface SVGProps {
  iconSource?: string;
  isOpen?: boolean;
}

export interface StyledInputProps {
  isChecked?: boolean;
  isDisabled?: boolean;
  isOpen?: boolean;
  isValid?: boolean;
  isVisible?: boolean;
  showError?: boolean;
  showPrefix?: boolean;
  slide?: boolean;
  center?: boolean;
}

interface AdditionalProps {
  customPattern?: string | RegExp;
  maxLength?: number;
  min?: number;
  max?: number;
  onPaste?: (e: React.ClipboardEvent<any>) => void;
}

export interface TextInputProps {
  name: string;
  value: string;
  type?: string;
  label?: string;
  customPattern?: string | RegExp;
  disabled?: boolean;
  readOnly?: boolean;
  autoComplete?: string;
  maxLength?: number;
  min?: number;
  max?: number;
  error?: any;
  touched: any;
  prefixText?: string;
  insetIcon?: string;
  resetLabel?: boolean;
  onFocus?: Function;
  onBlur?: (e: React.FocusEvent<any>) => void;
  onPaste?: (e: React.ClipboardEvent<any>) => void;
  onChange:
    | ((e: React.ChangeEvent<any>) => void)
    | (((e: React.ChangeEvent<any>) => void) & ((text: string) => void));
  hintContents?: React.ReactNode;
  toggleHint?: boolean;
  forwardRef?: MutableRefObject<any>;
  required?: boolean;
  heading?: string;
}

// #region Styles ---------------------------------------------------------------------------------
// Outer container spans width of parent
const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;
Container.displayName = 'Container';

// An optional heading above the input
export const OuterHeading = styled(SubHeading)`
  width: 100%;
  font-size: 0.875rem;
  line-height: 1.125rem;
  text-align: left;
  color: ${colors.TEXT_PRIMARY};
  margin-bottom: 0.375rem;
`;
OuterHeading.displayName = 'OuterHeading';

// Inner container includes label, input, and validation elements
// Relatively positioned to ensure children are properly positioned
const Content = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;
Content.displayName = 'Content';

// Resembles a placeholder for an unfocused Input with an empty input value
// For inputs with focus or non-empty values, the label shrinks and slides up above the input value
// Label changes text color to red when an error exists for a touched input
// Props:
// slide - moves label to make room for user input, i.e. onFocus, or for non-empty value
const Label = styled.label<StyledInputProps>`
  position: absolute;
  left: 1rem;
  display: flex;
  justify-content: flex-start;
  line-height: 3.5rem;
  font-size: 1rem;
  color: ${colors.TEXT_LABEL};
  pointer-events: none;
  transition: transform 0.175s ease-out;
  transform-origin: left top;
  white-space: nowrap;
  ${(p: StyledInputProps) =>
    p.showPrefix &&
    `
    left: 1.9375rem;
  `};
  ${(p: StyledInputProps) =>
    p.slide &&
    `
    left: 1rem;
    transform: translateY(-12%) scale(0.75);
  `};
`;
Label.displayName = 'Label';

// Contains text child positioned to the left of the input value
const PrefixContainer = styled.div<StyledInputProps>`
  position: absolute;
  top: 0;
  left: 0;
  height: 3.5rem;
  padding: 0 0 0 1rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  color: ${colors.TEXT_LABEL};
  pointer-events: none;
  ${(p: StyledInputProps) =>
    p.slide &&
    `
    transition: transform 0.175s ease-out;
    transform: translateY(0.5rem);
    color: ${colors.TEXT_PRIMARY};
  `};
`;
PrefixContainer.displayName = 'PrefixContainer';

// Input changes border color to red when an error exists for a touched input
// Upon hover/focus, border is emphasized with color change and box shadow
// When disabled, the hover behavior is no longer applied
// Props:
// showError - changes border color to red
const Input = styled.input<StyledInputProps>`
  height: 3.5rem;
  width: 100%;
  min-height: 3.5rem;
  border-radius: 0.125rem;
  border: 0.0625rem solid ${colors.BORDER_PRIMARY};
  padding: 1.75rem 2.1875rem 1rem 0.875rem;
  font-size: 1rem;
  color: ${colors.TEXT_INPUT};
  outline: none;
  appearance: none;

  ${(p: StyledInputProps) =>
    p.showPrefix &&
    `
    padding: 2rem 2.1875rem 1.0625rem 2rem;
  `};
  ${(p: StyledInputProps) =>
    p.showError &&
    `
    border-color: ${colors.TEXT_ERROR};
  `};

  /* Hover/Focus - change border color to outline color and adds box shadow */
  /* When an error exists, the border color and box shadow turn red */
  &:hover,
  &:focus {
    box-shadow: 0 0 0.25rem ${colors.UI_OUTLINE}26;
    border-color: ${colors.UI_OUTLINE};

    ${(p: StyledInputProps) =>
      p.showError &&
      `
      box-shadow: 0 0 0.25rem ${colors.TEXT_ERROR}26;
      border-color: ${colors.TEXT_ERROR};
    `};
  }

  /* Disabled - remove hovering border color change and box shadow */
  &:disabled {
    box-shadow: none;
    border-color: ${colors.BORDER_PRIMARY};
    background-color: rgba(239, 239, 239, 0.3);
    cursor: no-drop;
  }
`;
Input.displayName = 'Input';

// Reveals inset icon on the right side of text input box
// Props:
// visible - toggles display between flex and none
const InsetContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 3.5rem;
  padding: 1.25rem 1rem;
  display: flex;
  align-items: center;
  pointer-events: none;
`;
InsetContainer.displayName = 'InsetContainer';

const CustomInsetIcon = styled.div<SVGProps>`
  height: 100%;
  width: auto;
  pointer-events: none;

  ${(p: SVGProps) =>
    p.iconSource &&
    `
    content: url(${p.iconSource});
  `};
`;
CustomInsetIcon.displayName = 'CustomInsetIcon';

// Hidden error message is revealed when an error exists for a touched input
// Props:
// visible - toggles display between flex and none
export const ErrorMessage = styled(ErrorMessageBubble)<StyledInputProps>`
  display: ${(p: StyledInputProps) => (p.isVisible ? 'flex' : 'none')};
`;
ErrorMessage.displayName = 'ErrorMessage';
// #endregion

// Component --------------------------------------------------------------------------------------
export const TextInput: FC<TextInputProps> = ({
  name, // required, used for ID and for proper form functionality
  value, // required
  type, // input type
  label,
  customPattern, // regex only allows these characters to be entered
  disabled,
  readOnly,
  autoComplete,
  maxLength,
  min,
  max,
  error, // error message string, undefined when input passes validation
  touched,
  prefixText,
  insetIcon,
  resetLabel,
  onFocus,
  onBlur,
  onChange, // validation callback for current input value
  onPaste,
  hintContents,
  toggleHint,
  forwardRef,
  required,
  heading,
}) => {
  // State ----------------------------------------------------------------------------------------
  // Pass optional props to the input using this object
  const [extraProps, setExtraProps] = useState({});
  // Transform label when input is focused or has a nonempty value
  const [adjustLabel, setAdjustLabel] = useState(false);
  const [showHint, setShowHint] = useState(false);

  // Hooks ----------------------------------------------------------------------------------------
  const { setAriaAlertMessage } = useContext(AriaAlertContext);

  // Consolidate optional props into state variable extraProps
  useEffect(() => {
    const additionalProps: AdditionalProps = {};

    // Custom pattern
    if (customPattern) {
      additionalProps.customPattern = customPattern;
    }
    // Max length
    if (maxLength) {
      additionalProps.maxLength = maxLength;
    }
    // Min value
    if (min) {
      additionalProps.min = min;
    }
    // Max value
    if (max) {
      additionalProps.max = max;
    }
    // onPaste handler
    if (onPaste) {
      additionalProps.onPaste = onPaste;
    }

    // Set state with updated optional props to pass to the input
    setExtraProps(additionalProps);
  }, [customPattern, maxLength, min, max, onPaste]);

  // Slide up and shrink the input label if input value is nonempty
  useEffect(() => {
    if (value !== undefined && value.length > 0) {
      setAdjustLabel(true);
    } else if (
      !value &&
      forwardRef &&
      forwardRef.current &&
      document?.activeElement
    ) {
      // Do not adjust label if value is null but the input is currently focused
      const isActive = forwardRef.current.name === document.activeElement.id;
      setAdjustLabel(isActive);
    }
  }, [value]);

  // Reset sliding label when autocompleted value is cleared with an X button
  useEffect(() => {
    if (!value && forwardRef && forwardRef.current && document?.activeElement) {
      // Do not reset label if value is null but the input is currently focused
      const isActive = forwardRef.current.name === document.activeElement.id;
      if (isActive) setAdjustLabel(true);
    } else if (resetLabel && adjustLabel && !value) {
      setAdjustLabel(false);
    }
  }, [resetLabel]);

  // Functions ------------------------------------------------------------------------------------
  // Checks if a value works with the optional regex prop
  const matchesCustomPattern = (inputValue: string): boolean =>
    !customPattern ||
    !!inputValue.match(customPattern) ||
    inputValue.length === 0;

  // onFocus - controls label animation and calls focus handler from props
  const handleFocus = () => {
    // Move aside the label when input is focused
    setAdjustLabel(true);
    // show the hint (if there is one)
    setShowHint(true);
    if (onFocus) onFocus();
  };

  // onBlur - changes state to indicate input is touched and calls blur handler from props
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // get rid of the hint immediately
    setShowHint(false);
    // Reset label to initial position/scale if input value is still empty
    if (value === undefined || (value !== undefined && value.length === 0)) {
      setAdjustLabel(false);
    }
    if (error && setAriaAlertMessage) {
      setAriaAlertMessage(error);
    }
    // Trigger synthetic change for Heap tracking purposes
    trackChangeOnBlur(e);
    // Call blur handler from props with the entire event object
    if (onBlur) onBlur(e);
  };

  // processes any changes before propagating them back toward formik
  const handleChange = (e: React.ChangeEvent<any>) => {
    const changedInput = e.target.value;
    // if the new input is no good
    if (!matchesCustomPattern(changedInput)) {
      // don't propagate it
      e.preventDefault();
      return;
    }
    // otherwise propagate this change upward
    onChange(e);
  };

  // Catch-all workaround to inconsistent browser event tracking
  function trackChangeOnBlur(e: React.FocusEvent<HTMLInputElement>) {
    // Clone the blur event and dispatch it as a change on a corresponding input element clone
    try {
      // Cast the blur event clone as a change event
      const syntheticEvent = new Event('change', e.nativeEvent);
      // Get the ID and form of the blurred input field
      const { id, form } = e.target;
      // Get the corresponding element clone if one exists, otherwise create one
      let clone = getFieldClone(id);
      const firstBlurWithChange = !clone && value !== '';
      if (!clone) clone = cloneField(id, document.getElementById(id), form);
      // Dispatch the synthetic event to simulate the change when the clone/real values differ
      const cloneValue = clone.getAttribute('value');
      if (cloneValue !== value || firstBlurWithChange) {
        clone.dispatchEvent(syntheticEvent);
        clone.setAttribute('value', value);
      }
    } catch {
      // TO-DO error handler - probably not worth a NPC-Client-Error
    }
  }

  // Render ---------------------------------------------------------------------------------------
  return (
    <Container>
      {heading && <OuterHeading>{heading}</OuterHeading>}
      <Content>
        <Label
          htmlFor={name}
          slide={adjustLabel}
          showError={error && touched}
          showPrefix={prefixText !== undefined}
        >
          {label}
        </Label>
        <PrefixContainer slide={adjustLabel}>{prefixText}</PrefixContainer>
        <Input
          id={name}
          name={name}
          value={value}
          type={type}
          disabled={disabled}
          readOnly={readOnly}
          autoComplete={autoComplete}
          showError={error && touched}
          showPrefix={prefixText !== undefined}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          ref={forwardRef}
          aria-required={required}
          aria-invalid={error && touched}
          aria-describedby={error && touched ? `${name}-error` : null}
          {...extraProps}
        />
        {error && touched && (!hintContents || !showHint) && (
          <ErrorMessage isVisible>
            <ErrorMessageText id={`${name}-error`}>{error}</ErrorMessageText>
            <ErrorMessageBubbleIcon />
          </ErrorMessage>
        )}
        {hintContents && (showHint || toggleHint) && (
          <MessageBubble role="status" aria-live="polite">
            {hintContents}
          </MessageBubble>
        )}
        {insetIcon && (
          <InsetContainer>
            <CustomInsetIcon iconSource={insetIcon} />
          </InsetContainer>
        )}
      </Content>
    </Container>
  );
};

TextInput.defaultProps = {
  type: 'text',
  label: undefined,
  customPattern: undefined,
  disabled: false,
  autoComplete: 'off',
  maxLength: undefined,
  min: undefined,
  max: undefined,
  error: undefined,
  prefixText: undefined,
  insetIcon: undefined,
  onFocus: () => {},
  onBlur: () => {},
  onPaste: undefined,
  readOnly: false,
  required: true,
  heading: undefined,
  resetLabel: undefined,
  hintContents: undefined,
  toggleHint: undefined,
  forwardRef: undefined,
};
